import {
    Typography,
    FormControlLabel,
    Switch,
    Grid,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { FormikProps } from 'formik';
import { CustomerTypes } from '../../../../../types';
import { SettingsFieldCard } from './settings-field-card';

interface Props {
    formik: FormikProps<CustomerTypes.OnboardingForm>;
    /**
     * Every field on this tab is a v2 catalog setting, so the whole tab is
     * read-only now that Customer.settingsV2 is the source of truth and
     * Customer.settings is only a derived mirror. Defaults to disabled so a
     * caller that forgets the prop cannot reopen a legacy write path.
     */
    disabled?: boolean;
}

export const FeaturesSettingsTab: React.FC<Props> = ({ formik, disabled = true }) => {
    const { values } = formik;

    const handleFieldChange = (field: string, value: any) => {
        formik.setFieldValue(field, value);
    };

    return (
        <>
            <Typography fontWeight={500} fontSize={18} mt={2}>
                Dashboard & Features
            </Typography>
            <Typography fontSize={14} mb={1} color="text.secondary">
                Control which features and dashboard sections are visible to this organization.
            </Typography>

            {/* Insights Dashboard */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.showInsightsDashboard ?? true}
                            onChange={(e) => handleFieldChange('showInsightsDashboard', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Insights Dashboard</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Show the Insights Dashboard as the default landing page. When disabled, users land on the Event List instead.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Message Center */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.showMessageCenter ?? true}
                            onChange={(e) => handleFieldChange('showMessageCenter', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Message Center</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Show or hide the Message Center in the sidebar navigation for this organization.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Asset Management */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.assetManagementEnabled ?? false}
                            onChange={(e) => handleFieldChange('assetManagementEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Asset Management</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable asset tag management on the Devices page, allowing admins to assign and track asset tags for organization devices.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Intake Risk Assessment */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.showRiskAssessment ?? false}
                            onChange={(e) => handleFieldChange('showRiskAssessment', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Intake Risk Assessment (Safety Hub)</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the Intake Risk Assessment feature, which provides risk factor analysis, criminal records, and area safety data before a visit.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Intake Risk Assessment Address-Only Mode */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.showRiskAssessmentAddressOnly ?? false}
                            onChange={(e) => handleFieldChange('showRiskAssessmentAddressOnly', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Intake Risk Assessment Address-Only Mode</Typography>
                    <Typography color="textSecondary" variant="body2">
                        When enabled, the Intake Risk Assessment feature only accepts address input (no name or other identifiers).
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Appointments Activity Redesign */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.appointmentsCheckOnMeTrackingEnabled ?? false}
                            onChange={(e) => handleFieldChange('appointmentsCheckOnMeTrackingEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Appointments with Check-On-Me Tracking</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable enriched appointment views with Check-On-Me alert tracking, showing check-in/check-out times, escalation status, elapsed time, and summary statistics.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Hide Last Ping */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.hideLastPing ?? false}
                            onChange={(e) => handleFieldChange('hideLastPing', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Hide Last Ping</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Hide the Last Ping column from the POM Users table. When enabled, the last device ping timestamp will not be displayed.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            <Typography fontWeight={500} fontSize={18} mt={4}>
                Safety & Reports
            </Typography>
            <Typography fontSize={14} mb={1} color="text.secondary">
                Control safety search and reporting features for this organization.
            </Typography>

            {/* Intake Risk Assessment (Mobile) */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.isSafetyReportEnabled ?? true}
                            onChange={(e) => handleFieldChange('isSafetyReportEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Intake Risk Assessment (Mobile)</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the Intake Risk Assessment feature on the mobile app, allowing users to view safety data for a specific location.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Intake Risk Assessment Search (Mobile) */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.safetyReportEnabled ?? true}
                            onChange={(e) => handleFieldChange('safetyReportEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Intake Risk Assessment Search (Mobile)</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Allow mobile app users to search an address to review sex offender and crime data.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Activity Reports */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.isActivityReportEnabled ?? false}
                            onChange={(e) => handleFieldChange('isActivityReportEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Groups & Teams Activity</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the Groups & Teams Activity section under Reports, providing team activity and usage analytics.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Groups & Teams Dashboard */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.showGroupsTeamsDashboard ?? false}
                            onChange={(e) => handleFieldChange('showGroupsTeamsDashboard', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Groups & Teams Dashboard</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the Groups & Teams Dashboard under Reports, allowing drill-down into groups, teams, and individual user activity with detailed charts and filters.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Reports Button (Mobile Map) */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.reportsButtonEnabled ?? true}
                            onChange={(e) => handleFieldChange('reportsButtonEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Reports Button (Mobile Map)</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Show or hide the Reports button on the mobile app map screen.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Add Report Button (Mobile Reports Drawer) */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.addReportButtonEnabled ?? true}
                            onChange={(e) => handleFieldChange('addReportButtonEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Add Report Button (Mobile Reports Drawer)</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Show or hide the Add Report button inside the mobile reports drawer.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* POM Trigger (Mobile) — POM master only */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.pomTriggerEnabled ?? false}
                            onChange={(e) => handleFieldChange('pomTriggerEnabled', e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>POM Trigger (Mobile)</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the redesigned POM Trigger device flow in the mobile app for this customer. POM master only.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

        </>
    );
};
